import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {MbscCalendarEvent, MbscCalendarEventData} from "@mobiscroll/react";
import type {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {
    Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    formatDateHMRange,
    formatFullDMY,
} from "crqlar-react-common/dist/utils/date.util";
import {filterProfilesByIds} from "crqlar-react-common/dist/utils/profile.util";
import {useTranslation} from "react-i18next";
import ReservationEventPreview from "../ReservationEventPreview";
import {ReservationRailSearch} from "../rail/search/ReservationRailSearch";
import type {EventGenerationConfig} from "./util/calendarEventGenerator";
import {useCalendarRailSearchResults} from "./util/useCalendarRailSearchResults";

interface CalendarRailSearchProps {
    sector?: Sector;
    fromDate: Date;
    toDate: Date;
    selectedStatuses: ReservationStatus[];
    selectedResourceIds: string[];
    eventGenerationConfig: EventGenerationConfig;
    reservationEvents: MbscCalendarEvent[];
    profiles: Profile[];
    ageCategories: AgeCategory[];
    mbscResourceColumnType: "resources" | "services" | undefined;
    onSelectResult: (event: MbscCalendarEvent) => void;
    onEditReservation: (reservation: Reservation) => void;
}

export function CalendarRailSearch({
    sector,
    fromDate,
    toDate,
    selectedStatuses,
    selectedResourceIds,
    eventGenerationConfig,
    reservationEvents,
    profiles,
    ageCategories,
    mbscResourceColumnType,
    onSelectResult,
    onEditReservation,
}: CalendarRailSearchProps) {
    const {t} = useTranslation();
    const {
        search,
        query,
        reservations,
        eventsByReservationId,
        state,
    } = useCalendarRailSearchResults({
        sector,
        fromDate,
        toDate,
        selectedStatuses,
        selectedResourceIds,
        eventGenerationConfig,
        reservationEvents,
    });

    return (
        <ReservationRailSearch
            query={search.query}
            onQueryChange={search.setQuery}
            state={state}
            idleMessage={t("Enter a search term")}
            emptyMessage={t('No results for "{{query}}"', {
                query: search.debouncedQuery,
            })}
            onRetry={() => void query.refetch()}
        >
            {close => (
                <Stack>
                    {reservations.map(reservation => {
                        const locateEvent = reservation.id
                            ? eventsByReservationId.get(reservation.id)
                            : undefined;
                        const startDate = reservation.expectedStartDate
                            ? new Date(reservation.expectedStartDate)
                            : undefined;
                        const endDate = reservation.expectedEndDate
                            ? new Date(reservation.expectedEndDate)
                            : undefined;
                        const timeRange = formatDateHMRange(startDate, endDate);
                        const dateTimeLabel = [
                            formatFullDMY(startDate),
                            timeRange === "-" ? undefined : timeRange,
                        ].filter(Boolean).join(" · ");
                        const previewEvent = locateEvent
                            ? {title: locateEvent.title} as MbscCalendarEventData
                            : undefined;

                        return (
                            <Box
                                key={reservation.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-end",
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Stack sx={{flex: 1, minWidth: 0, gap: 0.5}}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{px: 0.5}}
                                    >
                                        {dateTimeLabel}
                                    </Typography>
                                    <ButtonBase
                                        disabled={!locateEvent}
                                        onClick={() => {
                                            if (!locateEvent) return;
                                            close();
                                            onSelectResult(locateEvent);
                                        }}
                                        sx={{
                                            width: "100%",
                                            height: 64,
                                            minWidth: 0,
                                            borderRadius: 1,
                                            textAlign: "left",
                                        }}
                                    >
                                        <ReservationEventPreview
                                            reservation={reservation}
                                            event={previewEvent}
                                            profiles={filterProfilesByIds(
                                                profiles,
                                                reservation.profileIds,
                                            )}
                                            mbscResourceColumnType={mbscResourceColumnType}
                                            ageCategories={ageCategories}
                                        />
                                    </ButtonBase>
                                </Stack>

                                <Tooltip title={t("Edit reservation")} arrow>
                                    <IconButton
                                        size="small"
                                        aria-label={t("Edit reservation")}
                                        onClick={() => {
                                            close();
                                            onEditReservation(reservation);
                                        }}
                                        sx={{mb: 2}}
                                    >
                                        <EditOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </ReservationRailSearch>
    );
}
